import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import type { AppUser } from '../types/user';
import { toVietnameseMessage, normalizeApiErrorMessage } from '../utils/errorMessageVi';

type LoginResult =
  | { success: true; user: AppUser }
  | { success: false; error: string };

type UserContextType = {
  user: AppUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  fetchUser: () => Promise<void>;
};

const UserContext = createContext<UserContextType | null>(null);

const normalizeUser = (input: any): AppUser => {
  const normalizedRole = input?.role;
  const siteId = input?.siteId ?? input?.site_id ?? null;
  const siteIds = Array.isArray(input?.site_ids) ? input.site_ids : [];
  const isGlobalRole = normalizedRole === 'SuperAdmin';

  return {
    ...input,
    role: normalizedRole,
    userId: input?.userId || input?.id,
    siteId,
    site_id: siteId,
    site_ids: siteIds,
    canAccessAllSites: Boolean(input?.canAccessAllSites ?? isGlobalRole),
  };
};

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      const normalized = normalizeUser(response.data?.data || {});
      setUser(normalized);
      localStorage.setItem('user', JSON.stringify(normalized));
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const cachedUser = localStorage.getItem('user');

    if (cachedUser) {
      try {
        setUser(normalizeUser(JSON.parse(cachedUser)));
      } catch {
        localStorage.removeItem('user');
      }
    }

    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username: string, password: string): Promise<LoginResult> => {
    try {
      const response = await api.post('/auth/login', { username, password });
      if (!response.data?.success) {
        return {
          success: false,
          error: toVietnameseMessage(response.data?.error?.message, 'Đăng nhập thất bại.'),
        };
      }

      const token = response.data?.data?.token;
      const nextUser = normalizeUser(response.data?.data?.user || {});

      if (nextUser.role === 'LocalAPI') {
        return {
          success: false,
          error: 'LocalAPI chỉ dùng cho tích hợp, không được đăng nhập giao diện.',
        };
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(nextUser));
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      setUser(nextUser);

      return { success: true, user: nextUser };
    } catch (error: any) {
      return {
        success: false,
        error: normalizeApiErrorMessage(error, 'Đăng nhập thất bại.'),
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    delete api.defaults.headers.common.Authorization;
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, loading, login, logout, fetchUser }),
    [user, loading]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUserContext must be used within UserProvider');
  }
  return ctx;
};

// Backward compatibility for existing components/pages
export const AuthProvider = UserProvider;
export const useAuth = useUserContext;


